import {
  MySizes,
  SizeCategory,
  SizeCategorySize,
  Universe,
  MySizesStepType,
  SizeObjType,
} from '@interfaces/models/mySizes';
import { genId, genIds } from '@components/catalog/my-sizes/my-sizes/editing/sizesMap';

const BOTH_UNIVERSES_IDS = '1-2';

// Generate label of toggle buttons (size category)
const generateSizeCategoryLabel = (value: MySizes, sizeId: string): string[] => {
  const sizeCategories: SizeCategory[] = value.size_categories.filter((v) => v.id === sizeId) || [];
  return sizeCategories.length ? sizeCategories[0].sizes.map((s) => s.label) : [];
};

// Generates options for toggle buttons based on size categories for a specific universe.
export const generateSizeTypeOptions = (
  options: MySizesStepType[],
  categoryIndex: number,
  value: MySizes,
  iconMap: Record<string, string>,
) => {
  if (!options || options.length <= categoryIndex) {
    return [];
  }

  return options[categoryIndex].size_categories.map((category) => {
    const label = generateSizeCategoryLabel(value, category.id).join(',');
    return {
      label,
      value: category.id,
      icon: iconMap[category.id],
      altText: category.label,
    };
  });
};

// Retrieves all available sizes for a specific size category within a universe.
export const getSizesByCategory = (
  options: MySizesStepType[],
  categoryIndex: number,
  sizeType: string,
): SizeObjType[] => {
  if (!options || !options.length || !options[categoryIndex]) {
    return [];
  }

  const categoryOptions = options[categoryIndex].size_categories.filter((s) => s.id === sizeType);
  return categoryOptions.length ? categoryOptions[0].sizes : [];
};

// Gets selected size IDs from value
export const getSelectedSizeIds = (value: MySizes, sizeId: string): string[] => {
  const categories = value?.size_categories || [];
  const category = categories.find((t) => t.id === sizeId);
  return category ? category.sizes.map((t) => t.id) : [];
};

// Gets universe label by universe ID
const getUniverseLabelById = (options: MySizesStepType[], universeIds: string): string => {
  const universeOption = options.find((option) => genId(option.universe.ids) === universeIds);
  return universeOption ? universeOption.universe.label : '';
};

// Gets universe object by universe ID
const getUniverseById = (options: MySizesStepType[], universeIds: string): Universe => {
  const universeOption = options.find((option) => genId(option.universe.ids) === universeIds);
  return universeOption ? universeOption.universe : { label: '', ids: [] };
};

// Gets category label by category ID
const getCategoryLabelById = (options: MySizesStepType[], categoryId: string): string => {
  for (const option of options) {
    const category = option.size_categories.find((s) => s.id === categoryId);
    if (category) {
      return category.label;
    }
  }
  return '';
};

// Creates an initial MySizes object
const createInitialMySizes = (options: MySizesStepType[], universeIds: string): MySizes => ({
  size_categories: [],
  universe: {
    label: getUniverseLabelById(options, universeIds),
    ids: genIds(universeIds),
  },
  filter: '',
});

// Update size category
const updateSizeCategory = (params: {
  categories: SizeCategory[];
  sizeType: string;
  size: SizeCategorySize;
  isSelected: boolean;
  options: MySizesStepType[];
}): SizeCategory[] => {
  const { categories, sizeType, size, isSelected, options } = params;
  const existingCategory = categories.find((category) => category.id === sizeType);

  const updatedCategory: SizeCategory = existingCategory
    ? { ...existingCategory }
    : {
        id: sizeType,
        label: getCategoryLabelById(options, sizeType),
        sizes: [],
      };

  if (isSelected) {
    updatedCategory.sizes = [...updatedCategory.sizes, size];
  } else {
    updatedCategory.sizes = updatedCategory.sizes.filter((sizeItem) => sizeItem.id !== size.id);
  }

  // Return categories without empty categories
  const filteredCategories = categories.filter((category) => category.id !== sizeType);

  // Only add the category back if it has sizes
  if (updatedCategory.sizes.length > 0) {
    return [...filteredCategories, updatedCategory];
  }

  return filteredCategories;
};

// Updates MySizes object with a new size selection (either adding or removing a size).
export const updateMySizesWithSizeSelection = (
  currentValue: MySizes | null,
  sizeType: string,
  size: SizeCategorySize,
  isSelected: boolean,
  options: MySizesStepType[],
  universeIds: string,
): MySizes => {
  const baseValue = currentValue || createInitialMySizes(options, universeIds);

  return {
    ...baseValue,
    size_categories: updateSizeCategory({
      categories: baseValue.size_categories,
      sizeType,
      size,
      isSelected,
      options,
    }),
  };
};

// Filters MySizes object to only include size categories relevant to a specific universe.
export const filterMySizesForUniverse = (
  mySizes: MySizes,
  universeIds: string,
  sizeTypes: string[][],
  universesMap: string[],
  options: MySizesStepType[],
): MySizes => {
  const universeObj = getUniverseById(options, universeIds);
  const universeIndex = universesMap.findIndex((id) => id === universeIds);
  const applicableSizeTypeIds = universeIndex >= 0 ? sizeTypes[universeIndex] : [];

  const filteredCategories =
    universeIds !== BOTH_UNIVERSES_IDS
      ? mySizes.size_categories.filter((category) => applicableSizeTypeIds.includes(category.id))
      : mySizes.size_categories;

  return {
    ...mySizes,
    universe: universeObj,
    size_categories: filteredCategories,
  };
};

// Creates an empty MySizes object for deletion
export const createEmptyMySizes = (): MySizes => ({
  universe: { ids: [], label: '' },
  size_categories: [],
  filter: '',
});
